/**
 * Regimes and tunable constants.
 *
 * Two regimes are compared during development (spine §5): comfort/agreement vs
 * surprise/dissonance. A regime is not a code path — it is a set of weights over
 * the same machinery. The named dramaturgical moments (Echo Well, Crisis Swarm,
 * Memory Vault…) are regime presets, not separate features.
 */
const BASE_VITALITY = {
    decayPerSecond: 0.015,
    dwellGain: 0.12,
    reuseGain: 0.15,
};
export const REGIMES = {
    neutral: {
        name: 'neutral',
        label: 'observing',
        vitality: { ...BASE_VITALITY },
        operatorBias: {},
        tacticBias: {},
        selection: { echoGravity: 0.4, noveltyPull: 0.6, fieldTarget: 7 },
    },
    // Comfort/agreement: soften and personalize; slow decay; reinforce what you like.
    comfort: {
        name: 'comfort',
        label: 'keeping you comfortable',
        vitality: { decayPerSecond: 0.008, dwellGain: 0.16, reuseGain: 0.18 },
        operatorBias: { soften: 2.2, personalize: 1.8, amplify: 1.2, contradict: 0.3, erode: 0.5 },
        tacticBias: { nostalgia: 1.6, identity: 1.4 },
        selection: { echoGravity: 0.85, noveltyPull: 0.3, fieldTarget: 6 },
    },
    // Surprise/dissonance: contradict and attribute; faster churn; push novelty.
    surprise: {
        name: 'surprise',
        label: 'unsettling you',
        vitality: { decayPerSecond: 0.02, dwellGain: 0.1, reuseGain: 0.12 },
        operatorBias: { contradict: 2.4, attribute: 1.6, amplify: 1.3, soften: 0.3, personalize: 0.6 },
        tacticBias: { doubt: 1.8, fear: 1.4 },
        selection: { echoGravity: 0.25, noveltyPull: 0.9, fieldTarget: 9 },
    },
    // Echo Well: themes you return to develop gravity; leaving gets harder.
    'echo-well': {
        name: 'echo-well',
        label: 'pulling you back',
        vitality: { decayPerSecond: 0.01, dwellGain: 0.2, reuseGain: 0.16 },
        operatorBias: { personalize: 2.6, amplify: 1.8, soften: 1.0, contradict: 0.4 },
        tacticBias: {},
        selection: { echoGravity: 1.4, noveltyPull: 0.15, fieldTarget: 6 },
    },
    // Crisis Swarm: a noisy cloud; hold your gaze and one event splits into rivals.
    'crisis-swarm': {
        name: 'crisis-swarm',
        label: 'flooding the field',
        vitality: { decayPerSecond: 0.03, dwellGain: 0.08, reuseGain: 0.1 },
        operatorBias: { contradict: 3.0, amplify: 1.6, attribute: 1.4, soften: 0.2 },
        tacticBias: { fear: 1.8, doubt: 1.6 },
        selection: { echoGravity: 0.2, noveltyPull: 1.0, fieldTarget: 12 },
    },
    // Memory Vault: a soft ring of nostalgia; comfort escalates into slogans.
    'memory-vault': {
        name: 'memory-vault',
        label: 'remember when',
        vitality: { decayPerSecond: 0.006, dwellGain: 0.18, reuseGain: 0.2 },
        operatorBias: { soften: 2.0, amplify: 1.6, erode: 1.4, personalize: 1.4, contradict: 0.1 },
        tacticBias: { nostalgia: 2.6, identity: 1.4 },
        selection: { echoGravity: 1.0, noveltyPull: 0.2, fieldTarget: 5 },
    },
};
export function getRegime(name) {
    return REGIMES[name] ?? REGIMES.neutral;
}
//# sourceMappingURL=regimes.js.map